import axios from "axios";

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_KEY = process.env.REACT_APP_LOGIN_HEADER_KEY;

// Creates axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "x-internal-key": API_KEY,
    "Content-Type": "application/json",
  },
  timeout: 15000, // 15 second timeout
});

// Adds request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  }
);

// Adds response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    if (error.response) {
      // Server responded with error status
      console.error(`API Error ${error.response.status}:`, error.response.data);
    } else if (error.request) {
      // Request made but no response
      console.error("No response from server:", error.request);
    } else {
      // Error setting up request
      console.error("Request setup error:", error.message);
    }
    return Promise.reject(error);
  }
);

/*Generate random 6-digit OTP*/
function generateOTP() {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

/*Generate unique client code using timestamp*/
function generateClientCode() {
  const timestamp = Date.now().toString();
  const random = Math.floor(Math.random() * 1000)
    .toString()
    .padStart(3, "0");
  return `WC${timestamp.slice(-6)}${random}`;
}

/*Check if phone number exists in database
@param {string} phone - Phone number without country code (10 digits)
@returns {Promise<boolean>} - True if user exists
 */
export const checkPhoneNumber = async (phone) => {
  try {
    const cleanPhone = phone.replace(/\D/g, "");
    console.log("Checking phone number:", cleanPhone);

    const response = await apiClient.get(`/clients/bymobile`, {
      params: { phone: cleanPhone },
    });

    if (response.data && response.data.data) {
      // Store user data in localStorage
      const userData = {
        id: response.data.data._id,
        name: response.data.data.client_name,
        email: response.data.data.email,
        phone: response.data.data.phone,
        client_code: response.data.data.client_code,
        status: response.data.data.status,
      };

      localStorage.setItem("authUser", JSON.stringify(userData));
      console.log("User found:", userData);
      return true;
    }

    return false;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log("Phone number not found (New User)");
      return false;
    }
    console.error("Error checking phone number:", error);
    return false;
  }
};

/*Create new client account
@param {string} name - Full name
@param {string} email - Email address
@param {string} phone - Phone number (10 digits)
@param {string} pan - PAN Card Number (Required by Backend)
@param {string} aadhar_no - Aadhaar Number (Required by Backend)
@returns {Promise<{success: boolean, data?: any, error?: string, clientCode?: string}>}
 */

export const createAccountFunction = async (
  name,
  email,
  phone,
  pan,
  aadhar_no
) => {
  try {
    const cleanPhone = phone.replace(/\D/g, "");
    const clientCode = generateClientCode();

    console.log("Creating account for:", {
      name,
      email,
      phone: cleanPhone,
      pan,
      aadhar_no,
      clientCode,
    });

    // First check if phone already exists
    const exists = await checkPhoneNumber(cleanPhone);
    if (exists) {
      return {
        success: false,
        error: "Phone number already registered. Please login instead.",
      };
    }

    // Prepare payload matching API structure
    const payload = {
      client_code: clientCode,
      client_name: name,
      email: email,
      phone: cleanPhone,
      status: "pending", // Will be updated to 'active' after OTP verification

      // --- Use arguments passed to function ---
      pan: pan || "1111",
      aadhar_no: aadhar_no || "1111",
      // ----------------------------------------

      dob: "",
      gender: "",
      maritalStatus: "",
      education: "",
      monthlyIncome: "",
      netWorth: "",
      riskProfile: "",
      investmentObjective: "",
      investmentHorizon: "",
      investmentExperience: "",
      investmentKnowledge: "",
      clientClub: "",
      occupation: "",
      location: "",

      // Arrays
      products: [],
      platform: [],
      familyTree: [],
      integrations: [],

      // Nested objects
      bank: {
        accountNumber: "",
        ifscCode: "",
        bankName: "",
      },

      investmentSummary: {
        totalValue: null,
        portfolio: [],
      },

      subscrpition: {
        isSubscribed: false,
        type: "normal",
        vbalidity: "30days",
      },

      // Additional fields
      department: "",
      assignedRM: "",
      assignedRMId: "",
      signature: "",
      fcmToken: "",
    };

    const response = await apiClient.post("/clients", payload);

    console.log("Account created successfully:", response.data);

    return {
      success: true,
      data: response.data,
      clientCode: clientCode,
    };
  } catch (error) {
    console.error(
      "Error creating account:",
      error.response?.data || error.message
    );

    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create account. Please try again.",
    };
  }
};

/*Send OTP to phone number
@param {string} phone - Phone number (10 digits)
@returns {Promise<{success: boolean, otp?: string, phoneNumber?: string, error?: string}>}
*/
export const sendOTPfunction = async (phone) => {
  try {
    const cleanPhone = phone.replace(/\D/g, "");
    const otp = generateOTP();
    const formattedPhone = `+91${cleanPhone}`;

    console.log("Sending OTP:", { phone: formattedPhone, otp });

    const response = await apiClient.post("/otp/send", {
      phoneNumber: formattedPhone,
      otp: otp,
    });

    console.log("OTP sent successfully");
    console.log("🔐 OTP Code:", otp);

    return {
      success: true,
      otp: otp,
      phoneNumber: formattedPhone,
      message: response.data?.message || "OTP sent successfully",
    };
  } catch (error) {
    console.error("Error sending OTP:", error.response?.data || error.message);

    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to send OTP. Please try again.",
    };
  }
};

/*Resend OTP to phone number
@param {string} phone - Phone number with or without country code
@returns {Promise<{success: boolean, otp?: string, phoneNumber?: string, error?: string}>}
*/
export const resendOTPfunction = async (phone) => {
  try {
    const cleanPhone = phone.replace(/\D/g, "");
    const otp = generateOTP();
    const formattedPhone = `+91${cleanPhone}`;

    console.log("Resending OTP:", { phone: formattedPhone, otp });

    const response = await apiClient.post("/otp/send", {
      phoneNumber: formattedPhone,
      otp: otp,
    });

    console.log("OTP resent successfully");
    console.log("🔐 New OTP Code:", otp);

    return {
      success: true,
      otp: otp,
      phoneNumber: formattedPhone,
      message: response.data?.message || "OTP sent successfully",
    };
  } catch (error) {
    console.error(
      "Error resending OTP:",
      error.response?.data || error.message
    );

    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to resend OTP. Please try again.",
      phoneNumber: phone,
    };
  }
};

/*registration logic
@param {string} phone - Phone number with country code
@returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const completeRegistrationHandshake = async (phone) => {
  try {
    const cleanPhone = phone.replace(/\D/g, "").slice(-10);

    console.log("Skipping server verification. Fetching user:", cleanPhone);

    // checkPhoneNumber handles fetching user data and saving it to localStorage.
    const userExists = await checkPhoneNumber(cleanPhone);

    if (userExists) {
      return {
        success: true,
        message: "User successfully verified and logged in",
      };
    } else {
      throw new Error("User created but could not be retrieved.");
    }
  } catch (error) {
    console.error(
      "Registration handshake failed:",
      error.response?.data || error.message
    );

    return {
      success: false,
      error: "Could not retrieve account details. Please try logging in.",
    };
  }
};

/*
 Get current authenticated user from localStorage
 @returns {object|null} User data or null
 */
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem("authUser");
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error("Error reading user data:", error);
    return null;
  }
};

/* Clear authentication data*/
export const logout = () => {
  localStorage.removeItem("authUser");
  console.log("User logged out");
};

// Export API client for advanced usage
export { apiClient };
